// Muster (attendance) report parsing — pure functions, no React/Supabase, so
// the logic is testable in Node (scripts/check-muster-parse.mjs runs it
// against the real Trial Sheet workbooks plus synthetic edge cases).
//
// Everything is matched by HEADER NAME, never by column position: the same
// workbook's July sheet has "FD" at column 5 with dates starting at column 16,
// while August has "Full Days" at column 5 with dates from column 15. Nothing
// about position is assumed.
//
// The day CELLS are authoritative. The sheet's stated summary columns
// (Full Days / ML / CL / L1 / LWP / ...) are unreliable — August records
// V025's 20 not-employed dashes as "LWP 20" — so this parser counts the day
// cells and compares against the stated summary; any disagreement is a
// blocker flag on that employee (no payslip until the sheet is fixed).
// Nothing is silently picked.
//
// This module knows nothing about the employee database, leave types, YTD
// allocations, or pay — those checks join DB data and live in the calculation
// layer (Stage C). Flags emitted here are only the ones the file alone proves.
import * as XLSX from 'xlsx';

// ─── Day codes ────────────────────────────────────────────────────────────────
// FD full day, L1 monthly paid leave, CL casual, ML medical, WO week off: PAID.
// LWP leave without pay and A absent: UNPAID (treated identically).
// '-' : not employed on that date — neither paid nor unpaid, and any dash
// blocks the payslip (mid-month joiner/leaver, manual handling).
// ANYTHING else — including an empty cell, a stray number (K007 has 0/0/0/4/0
// in real July data), or a future half-day code with no pay rule — is an
// unknown cell and blocks the payslip. Refuse loudly, never guess.
export const PAID_CODES = ['FD', 'L1', 'CL', 'ML', 'WO'];
export const UNPAID_CODES = ['LWP', 'A'];
export const NOT_EMPLOYED = '-';

const CODE_TO_COUNT = { FD: 'fd', L1: 'l1', CL: 'cl', ML: 'ml', WO: 'wo', LWP: 'lwp', A: 'absent' };

export const normHeader = (h) => String(h ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
const cellText = (v) => (v === null || v === undefined ? '' : String(v).trim());

// ─── Expected columns ─────────────────────────────────────────────────────────
// Required to parse at all. "Leaves" and "Holidays" exist in the files but are
// deliberately NOT read: no day code maps to them, so any comparison would be
// a guess about the sheet's formula — exactly what this parser refuses to do.
export const REQUIRED_COLUMNS = [
  { key: 'employee_id', header: 'Employee ID' },
  { key: 'name',        header: 'Employee Name' },
  { key: 'ml',          header: 'ML' },
  { key: 'cl',          header: 'CL' },
  { key: 'l1',          header: 'L1' },
  { key: 'lwp',         header: 'LWP' },
  { key: 'half_days',   header: 'Half Days' },
  { key: 'absent_days', header: 'Absent Days' },
  { key: 'week_off',    header: 'Week Off' },
];
// The full-days summary appears as "Full Days" or "FD" — July has BOTH (with
// duplicate values). At least one is required; every one present is compared.
export const FULL_DAY_HEADERS = ['Full Days', 'FD'];
const OPTIONAL_COLUMNS = [
  { key: 'department',  header: 'Department' },
  { key: 'designation', header: 'Designation' },
];

// Date headers look like "01-08-2026 \n Saturday" — DD-MM-YYYY then the day
// name on a wrapped line. The May sheet's "01 \n May \n Friday" format matches
// nothing here, so that sheet reports zero date columns and is refused.
const DATE_HEADER_RE = /^\s*(\d{2})-(\d{2})-(\d{4})(?:\s|$)/;

const daysInMonth = (year, month1) => new Date(year, month1, 0).getDate();

// ─── Header discovery ─────────────────────────────────────────────────────────
function findHeaders(raw) {
  let best = { headerIdx: -1, colMap: {}, fullDayCols: [], dateCols: [], score: 0 };
  for (let i = 0; i < Math.min(raw.length, 10); i++) {
    const colMap = {};
    const fullDayCols = [];
    const dateCols = [];
    for (let j = 0; j < raw[i].length; j++) {
      const cell = raw[i][j];
      const norm = normHeader(cell);
      const req = REQUIRED_COLUMNS.find((c) => normHeader(c.header) === norm);
      const opt = OPTIONAL_COLUMNS.find((c) => normHeader(c.header) === norm);
      if (req && colMap[req.key] === undefined) { colMap[req.key] = j; continue; }
      if (opt && colMap[opt.key] === undefined) { colMap[opt.key] = j; continue; }
      const fd = FULL_DAY_HEADERS.find((h) => normHeader(h) === norm);
      if (fd) { fullDayCols.push({ col: j, header: fd }); continue; }
      const m = DATE_HEADER_RE.exec(String(cell ?? ''));
      if (m) dateCols.push({ col: j, date: `${m[1]}-${m[2]}-${m[3]}`, day: Number(m[1]), month: Number(m[2]), year: Number(m[3]) });
    }
    const score = REQUIRED_COLUMNS.filter((c) => colMap[c.key] !== undefined).length
      + (fullDayCols.length ? 1 : 0);
    if (score > best.score) best = { headerIdx: i, colMap, fullDayCols, dateCols, score };
  }
  return best;
}

// ─── Date-column validation (whole-file guards) ───────────────────────────────
// Returns { month, dim, problems }. Any problem refuses the entire sheet:
//   * duplicate dates (July repeats 27–31 as a second block — parsing them
//     would double-count anyone's leave in that window),
//   * dates from more than one month,
//   * impossible dates (day/month out of range),
//   * days of the month missing (a dropped column would silently undercount
//     everyone's attendance).
function validateDateCols(dateCols) {
  const problems = [];
  if (!dateCols.length) {
    return { month: null, dim: 0, problems: ['No date columns recognised (expected headers like "01-08-2026" with the day name below). Is this a muster report sheet?'] };
  }

  const impossible = dateCols.filter((d) => d.month < 1 || d.month > 12 || d.day < 1 || d.day > daysInMonth(d.year, d.month));
  if (impossible.length) {
    problems.push(`Impossible date column header(s): ${impossible.map((d) => d.date).join(', ')}.`);
  }
  const valid = dateCols.filter((d) => !impossible.includes(d));

  const months = [...new Set(valid.map((d) => `${d.year}-${String(d.month).padStart(2, '0')}`))];
  if (months.length > 1) {
    problems.push(`Date columns span more than one month (${months.join(', ')}) — a muster covers exactly one month.`);
  }

  const seen = new Map();
  for (const d of valid) seen.set(d.date, (seen.get(d.date) || 0) + 1);
  const dups = [...seen.entries()].filter(([, n]) => n > 1).map(([date]) => date);
  if (dups.length) {
    problems.push(`Duplicate date column(s): ${dups.join(', ')}. Counting them would double-count attendance in that window — remove the repeated columns.`);
  }

  let month = null;
  let dim = 0;
  if (months.length === 1) {
    month = months[0];
    const [y, m] = month.split('-').map(Number);
    dim = daysInMonth(y, m);
    const have = new Set(valid.map((d) => d.day));
    const missing = [];
    for (let day = 1; day <= dim; day++) {
      if (!have.has(day)) missing.push(`${String(day).padStart(2, '0')}-${String(m).padStart(2, '0')}-${y}`);
    }
    if (missing.length) {
      problems.push(`Missing date column(s): ${missing.join(', ')}. Every day of the month must be present or attendance is undercounted.`);
    }
  }

  return { month, dim, problems };
}

const sheetRaw = (ws) => XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false });

// ─── Workbook reader (feeds the sheet picker) ─────────────────────────────────
// Describes every sheet: missing required columns, date-column problems, and
// employee row count. The screen preselects only when exactly one sheet is
// complete — same pattern as the salary-sheet picker in payrollParse.js.
export function readMusterWorkbook(buffer) {
  const wb = XLSX.read(new Uint8Array(buffer), { type: 'array' });
  const sheets = wb.SheetNames.map((name) => {
    const raw = sheetRaw(wb.Sheets[name]);
    const { headerIdx, colMap, fullDayCols, dateCols } = findHeaders(raw);
    const missing = REQUIRED_COLUMNS.filter((c) => colMap[c.key] === undefined).map((c) => c.header);
    if (!fullDayCols.length) missing.push('Full Days (or FD)');
    const { month, problems } = headerIdx === -1
      ? { month: null, problems: ['No header row recognised.'] }
      : validateDateCols(dateCols);
    let employeeRows = 0;
    if (headerIdx !== -1) {
      for (let i = headerIdx + 1; i < raw.length; i++) {
        if (raw[i].some((c) => cellText(c) !== '')) employeeRows++;
      }
    }
    return { name, missing, dateProblems: problems, month, employeeRows, complete: missing.length === 0 && problems.length === 0 };
  });
  return { wb, sheets };
}

// ─── Stated-summary comparison ────────────────────────────────────────────────
// Blank stated cells read as 0 (the sheets leave zeros blank sometimes);
// non-numeric stated values are a mismatch in their own right.
const statedNumber = (v) => {
  const t = cellText(v);
  if (t === '') return 0;
  const n = Number(t.replace(/,/g, ''));
  return Number.isFinite(n) ? n : NaN;
};

// ─── Sheet parser ─────────────────────────────────────────────────────────────
// Returns { error, problems, month } when the whole sheet is refused, else:
//   { month, daysInMonth, rows, rowProblems }
// `month` on a refusal is the month the date headers name (null when they
// name none or more than one) so the screen can say "this sheet is for
// August" ahead of the sheet's own faults — a September muster carrying
// August's header row was reported as "missing 31-08-2026" (02-10-2026),
// which is true but sent the uploader looking at the parser instead of the
// headers.
// rows[i]: { musterEmployeeId, name, department, designation,
//            counts: { fd,l1,cl,ml,wo,lwp,absent,dash },
//            unknownCells: [{ date, value }],
//            statedSummary: { <header>: <raw value> },
//            flags: [{ code, level: 'blocker'|'warning', message }] }
// rowProblems: rows that could not become attendance rows at all (no Employee
// ID) — reported, never silently dropped.
export function parseMusterSheet(wb, sheetName) {
  const ws = wb.Sheets[sheetName];
  if (!ws) return { error: `Sheet "${sheetName}" not found in this workbook.`, problems: [] };

  const raw = sheetRaw(ws);
  if (!raw.length) return { error: `Sheet "${sheetName}" is empty.`, problems: [] };

  const { headerIdx, colMap, fullDayCols, dateCols } = findHeaders(raw);
  const missingCols = REQUIRED_COLUMNS.filter((c) => colMap[c.key] === undefined).map((c) => c.header);
  if (!fullDayCols.length) missingCols.push('Full Days (or FD)');
  if (headerIdx === -1 || missingCols.length) {
    const problems = [`Expected column(s) not found: ${missingCols.map((h) => `"${h}"`).join(', ') || 'no header row recognised'}.`];
    return { error: `Refusing "${sheetName}" — ${problems[0]} Nothing was guessed or skipped.`, problems };
  }

  const { month, dim, problems } = validateDateCols(dateCols);
  if (problems.length) {
    return { error: `Refusing "${sheetName}" — ${problems.join(' ')}`, problems, month };
  }

  const rows = [];
  const rowProblems = [];
  const rowIndexById = new Map(); // duplicate Employee ID detection

  for (let i = headerIdx + 1; i < raw.length; i++) {
    const r = raw[i];
    const get = (key) => (colMap[key] === undefined ? '' : r[colMap[key]]);
    const musterEmployeeId = cellText(get('employee_id'));
    const name = cellText(get('name'));
    if (!musterEmployeeId && !name && !r.some((c) => cellText(c) !== '')) continue; // blank row

    if (!musterEmployeeId) {
      if (name || r.some((c) => cellText(c) !== '')) {
        rowProblems.push({ row: i + 1, name: name || `row ${i + 1}`, message: 'No Employee ID — this row cannot be stored or matched. Fix the sheet.' });
      }
      continue;
    }

    // ── Count the day cells (authoritative) ──────────────────────────────────
    const counts = { fd: 0, l1: 0, cl: 0, ml: 0, wo: 0, lwp: 0, absent: 0, dash: 0 };
    const unknownCells = [];
    for (const d of dateCols) {
      const v = cellText(r[d.col]).toUpperCase();
      if (v === NOT_EMPLOYED) counts.dash++;
      else if (CODE_TO_COUNT[v]) counts[CODE_TO_COUNT[v]]++;
      else unknownCells.push({ date: d.date, value: v === '' ? '(empty)' : cellText(r[d.col]) });
    }

    // ── Stated summary, verbatim, for the mismatch display ───────────────────
    const statedSummary = {};
    for (const fd of fullDayCols) statedSummary[fd.header] = cellText(r[fd.col]);
    for (const c of REQUIRED_COLUMNS) {
      if (['ml', 'cl', 'l1', 'lwp', 'half_days', 'absent_days', 'week_off'].includes(c.key)) {
        statedSummary[c.header] = cellText(get(c.key));
      }
    }

    // ── Flags ────────────────────────────────────────────────────────────────
    const flags = [];

    // Summary vs counted. Every full-days variant present is compared; no
    // half-day code exists, so a non-zero stated Half Days can never be
    // supported by the cells and is a mismatch by construction.
    const cmp = (label, rawVal, counted) => ({ label, raw: cellText(rawVal), stated: statedNumber(rawVal), counted });
    const comparisons = [
      ...fullDayCols.map((fd) => cmp(fd.header, r[fd.col], counts.fd)),
      cmp('ML', get('ml'), counts.ml),
      cmp('CL', get('cl'), counts.cl),
      cmp('L1', get('l1'), counts.l1),
      cmp('LWP', get('lwp'), counts.lwp),
      cmp('Half Days', get('half_days'), 0),
      cmp('Absent Days', get('absent_days'), counts.absent),
      cmp('Week Off', get('week_off'), counts.wo),
    ];
    const mismatches = comparisons
      .filter((c) => Number.isNaN(c.stated) || c.stated !== c.counted)
      .map((c) => (Number.isNaN(c.stated)
        ? `${c.label}: sheet says "${c.raw}" (not a number), day cells count ${c.counted}`
        : `${c.label}: sheet says ${c.stated}, day cells count ${c.counted}`));
    if (mismatches.length) {
      flags.push({
        code: 'summary_mismatch',
        level: 'blocker',
        message: `Stated summary disagrees with the day cells — ${mismatches.join('; ')}. The day cells are counted, the summary is not trusted; no payslip until the sheet is corrected.`,
      });
    }

    if (counts.dash > 0) {
      flags.push({
        code: 'not_employed_days',
        level: 'blocker',
        message: `${counts.dash} day(s) marked "-" (not employed) — mid-month joiner or leaver. No payslip; handle manually.`,
      });
    }

    if (unknownCells.length) {
      flags.push({
        code: 'unknown_day_codes',
        level: 'blocker',
        message: `Unrecognised day cell(s): ${unknownCells.map((u) => `${u.date} = "${u.value}"`).join(', ')}. Known codes are ${PAID_CODES.join('/')} (paid), ${UNPAID_CODES.join('/')} (unpaid), "-" (not employed). No payslip until resolved.`,
      });
    }

    // L1 allocation is 4 per month; a 5th should have been LWP in the muster.
    // Warning, not blocker: the muster stays authoritative for pay.
    if (counts.l1 > 4) {
      flags.push({
        code: 'l1_over_monthly',
        level: 'warning',
        message: `${counts.l1} L1 days this month — the allocation is 4. The extra should appear as LWP in the muster; correct the sheet before generating slips if pay should change.`,
      });
    }

    const dupOf = rowIndexById.get(musterEmployeeId);
    if (dupOf !== undefined) {
      const msg = `Employee ID ${musterEmployeeId} appears more than once in the sheet — neither row can be trusted. No payslip until the sheet is fixed.`;
      flags.push({ code: 'duplicate_muster_row', level: 'blocker', message: msg });
      const first = rows[dupOf];
      if (!first.flags.some((f) => f.code === 'duplicate_muster_row')) {
        first.flags.push({ code: 'duplicate_muster_row', level: 'blocker', message: msg });
      }
    } else {
      rowIndexById.set(musterEmployeeId, rows.length);
    }

    rows.push({
      musterEmployeeId,
      name,
      department: cellText(get('department')),
      designation: cellText(get('designation')),
      counts,
      unknownCells,
      statedSummary,
      flags,
    });
  }

  return { month, daysInMonth: dim, rows, rowProblems };
}
